import type { CreasePattern, SelectionRef } from '@/origami/types';
import { assignmentColor } from '@/origami/types';
import type { PatternAnalysis } from '@/origami-math/analysis';
import type { DebugSettings } from '@/state/store';
import { worldToScreen, type Viewport } from './viewport';
import type { SnapResult } from './snapping';
import { patternExtent } from './snapping';
import { polygonCentroid } from '@/geometry/polygon';
import { toDegrees } from '@/constants/numeric';
import type { Vec2 } from '@/geometry/vec2';

export interface RenderOptions {
  pattern: CreasePattern;
  analysis: PatternAnalysis;
  viewport: Viewport;
  selection: SelectionRef[];
  hover: SelectionRef | null;
  highlightVertex: number | null;
  highlightEdge: number | null;
  showGrid: boolean;
  gridDivisions: number;
  showAuxiliary: boolean;
  snap: SnapResult | null;
  draft: { a: Vec2; b: Vec2; assignment: string } | null;
  measure: { a: Vec2; b: Vec2 } | null;
  debug: DebugSettings;
  theme: 'dark' | 'light';
  devicePixelRatio: number;
}

interface Palette {
  background: string;
  grid: string;
  gridStrong: string;
  text: string;
  face: string;
  faceHover: string;
  vertex: string;
  vertexInterior: string;
  select: string;
  warn: string;
  guide: string;
}

const THEME: Record<'dark' | 'light', Palette> = {
  dark: {
    background: '#0f1217',
    grid: '#1c2129',
    gridStrong: '#262d38',
    text: '#98a1b0',
    face: 'rgba(120, 140, 175, 0.07)',
    faceHover: 'rgba(76, 141, 255, 0.14)',
    vertex: '#8b93a1',
    vertexInterior: '#c7cedb',
    select: '#4c8dff',
    warn: '#e0a91b',
    guide: '#4c8dff',
  },
  light: {
    background: '#fbfbfd',
    grid: '#e8eaf0',
    gridStrong: '#d8dbe4',
    text: '#5a6270',
    face: 'rgba(90, 110, 150, 0.06)',
    faceHover: 'rgba(31, 111, 235, 0.12)',
    vertex: '#5a6270',
    vertexInterior: '#1a1e26',
    select: '#1f6feb',
    warn: '#b8860b',
    guide: '#1f6feb',
  },
};

/** Draw the whole crease pattern. Pure function of the options - no state. */
export function renderPattern(ctx: CanvasRenderingContext2D, o: RenderOptions): void {
  const { viewport: vp, pattern } = o;
  const c = THEME[o.theme];
  const dpr = o.devicePixelRatio;

  ctx.save();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, vp.width, vp.height);
  ctx.fillStyle = c.background;
  ctx.fillRect(0, 0, vp.width, vp.height);

  const S = (p: Vec2): Vec2 => worldToScreen(vp, p);

  if (o.showGrid) drawGrid(ctx, o, c);

  // Faces.
  const selectedFaces = new Set(
    o.selection.filter((s) => s.kind === 'face').map((s) => s.index),
  );
  for (let f = 0; f < pattern.facesVertices.length; f++) {
    const loop = pattern.facesVertices[f];
    if (loop.length < 3) continue;
    ctx.beginPath();
    loop.forEach((vi, i) => {
      const p = S(pattern.verticesCoords[vi]);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
    const hovered = o.hover?.kind === 'face' && o.hover.index === f;
    ctx.fillStyle = hovered || selectedFaces.has(f) ? c.faceHover : c.face;
    ctx.fill();
  }

  // Edges.
  const selectedEdges = new Set(
    o.selection.filter((s) => s.kind === 'edge').map((s) => s.index),
  );
  const baseWidth = 1.7;
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    const assignment = pattern.edgesAssignment[e];
    if (assignment === 'J' && !o.showAuxiliary) continue;
    const [a, b] = pattern.edgesVertices[e];
    const p1 = S(pattern.verticesCoords[a]);
    const p2 = S(pattern.verticesCoords[b]);
    const isSelected = selectedEdges.has(e);
    const isHover = o.hover?.kind === 'edge' && o.hover.index === e;
    const isHighlight = o.highlightEdge === e;

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    if (isSelected || isHighlight) {
      ctx.strokeStyle = c.select;
      ctx.lineWidth = baseWidth + 3.5;
      ctx.globalAlpha = 0.35;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = assignmentColor(assignment, o.theme);
    ctx.lineWidth = isHover || isSelected ? baseWidth + 1 : baseWidth;
    ctx.setLineDash(assignment === 'C' ? [7, 5] : assignment === 'J' ? [2, 4] : []);
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Vertices.
  const selectedVertices = new Set(
    o.selection.filter((s) => s.kind === 'vertex').map((s) => s.index),
  );
  for (let v = 0; v < pattern.verticesCoords.length; v++) {
    const topo = o.analysis.topology.vertices[v];
    const p = S(pattern.verticesCoords[v]);
    const interior = topo?.interior ?? false;
    const selected = selectedVertices.has(v);
    const hovered = o.hover?.kind === 'vertex' && o.hover.index === v;
    const highlighted = o.highlightVertex === v;

    const analysis = o.analysis.vertices[v];
    const failing =
      (analysis?.kawasaki.applicable && !analysis.kawasaki.satisfied) ||
      (analysis?.maekawa.applicable && !analysis.maekawa.satisfied);

    let r = interior ? 3 : 2.2;
    if (selected || hovered || highlighted) r += 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fillStyle = selected || highlighted
      ? c.select
      : failing
        ? c.warn
        : interior
          ? c.vertexInterior
          : c.vertex;
    ctx.fill();
    if (failing && !selected) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, r + 3.5, 0, Math.PI * 2);
      ctx.strokeStyle = c.warn;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  // Draft crease being drawn.
  if (o.draft) {
    const p1 = S(o.draft.a);
    const p2 = S(o.draft.b);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle = assignmentColor(o.draft.assignment as 'M', o.theme) ?? c.select;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    const dx = o.draft.b.x - o.draft.a.x;
    const dy = o.draft.b.y - o.draft.a.y;
    label(
      ctx,
      c,
      (p1.x + p2.x) / 2,
      (p1.y + p2.y) / 2 - 12,
      `${Math.hypot(dx, dy).toFixed(4)}  ${toDegrees(Math.atan2(dy, dx)).toFixed(1)}°`,
    );
  }

  // Measurement.
  if (o.measure) {
    const p1 = S(o.measure.a);
    const p2 = S(o.measure.b);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.strokeStyle = c.guide;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
    const dx = o.measure.b.x - o.measure.a.x;
    const dy = o.measure.b.y - o.measure.a.y;
    label(
      ctx,
      c,
      (p1.x + p2.x) / 2,
      (p1.y + p2.y) / 2 - 12,
      `len ${Math.hypot(dx, dy).toFixed(5)}   ang ${toDegrees(Math.atan2(dy, dx)).toFixed(2)}°`,
    );
  }

  // Snap indicator and guides.
  if (o.snap && o.snap.kind !== 'free') {
    ctx.strokeStyle = c.guide;
    ctx.fillStyle = c.guide;
    for (const g of o.snap.guides) {
      if (g.kind === 'line' && g.b) {
        const a = S(g.a);
        const b = S(g.b);
        ctx.beginPath();
        // Extend the guide well past the cursor so it reads as a construction line.
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        ctx.moveTo(a.x - dx * 4, a.y - dy * 4);
        ctx.lineTo(a.x + dx * 4, a.y + dy * 4);
        ctx.setLineDash([2, 5]);
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.5;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
      }
    }
    const sp = S(o.snap.point);
    ctx.beginPath();
    ctx.rect(sp.x - 4.5, sp.y - 4.5, 9, 9);
    ctx.lineWidth = 1.4;
    ctx.stroke();
    if (o.snap.label) label(ctx, c, sp.x + 10, sp.y - 10, o.snap.label);
  }

  drawDebug(ctx, o, c);
  ctx.restore();
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  o: RenderOptions,
  c: Palette,
): void {
  const vp = o.viewport;
  const extent = patternExtent(o.pattern);
  const divisions = Math.max(1, o.gridDivisions);
  let cell = extent / divisions;
  // Keep grid lines at least 6 screen pixels apart.
  while (cell * vp.scale < 6) cell *= 2;

  const topLeft = { x: (0 - vp.tx) / vp.scale, y: -(0 - vp.ty) / vp.scale };
  const bottomRight = { x: (vp.width - vp.tx) / vp.scale, y: -(vp.height - vp.ty) / vp.scale };
  const x0 = Math.floor(Math.min(topLeft.x, bottomRight.x) / cell) * cell;
  const x1 = Math.ceil(Math.max(topLeft.x, bottomRight.x) / cell) * cell;
  const y0 = Math.floor(Math.min(topLeft.y, bottomRight.y) / cell) * cell;
  const y1 = Math.ceil(Math.max(topLeft.y, bottomRight.y) / cell) * cell;

  ctx.lineWidth = 1;
  for (let x = x0; x <= x1 + cell * 0.5; x += cell) {
    const sx = Math.round(x * vp.scale + vp.tx) + 0.5;
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, vp.height);
    ctx.strokeStyle = Math.abs(x) < cell * 0.01 ? c.gridStrong : c.grid;
    ctx.stroke();
  }
  for (let y = y0; y <= y1 + cell * 0.5; y += cell) {
    const sy = Math.round(-y * vp.scale + vp.ty) + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(vp.width, sy);
    ctx.strokeStyle = Math.abs(y) < cell * 0.01 ? c.gridStrong : c.grid;
    ctx.stroke();
  }
}

function drawDebug(
  ctx: CanvasRenderingContext2D,
  o: RenderOptions,
  c: Palette,
): void {
  const { debug, pattern, viewport: vp } = o;
  const S = (p: Vec2): Vec2 => worldToScreen(vp, p);
  if (debug.vertexIds) {
    for (let v = 0; v < pattern.verticesCoords.length; v++) {
      const p = S(pattern.verticesCoords[v]);
      label(ctx, c, p.x + 6, p.y - 6, `v${v}`);
    }
  }
  if (debug.edgeIds) {
    for (let e = 0; e < pattern.edgesVertices.length; e++) {
      const [a, b] = pattern.edgesVertices[e];
      const p1 = pattern.verticesCoords[a];
      const p2 = pattern.verticesCoords[b];
      const p = S({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
      label(ctx, c, p.x + 4, p.y - 4, `e${e}`);
    }
  }
  if (debug.faceIds) {
    for (let f = 0; f < pattern.facesVertices.length; f++) {
      const loop = pattern.facesVertices[f].map((i) => pattern.verticesCoords[i]);
      const p = S(polygonCentroid(loop));
      label(ctx, c, p.x, p.y, `f${f}`);
    }
  }
}

function label(
  ctx: CanvasRenderingContext2D,
  c: Palette,
  x: number,
  y: number,
  text: string,
): void {
  ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';
  const w = ctx.measureText(text).width;
  ctx.fillStyle = c.background;
  ctx.globalAlpha = 0.82;
  ctx.fillRect(x - 3, y - 9, w + 6, 13);
  ctx.globalAlpha = 1;
  ctx.fillStyle = c.text;
  ctx.fillText(text, x, y);
}
